import { motion } from "framer-motion";
import { Heart, Music2, Users, Flame } from "lucide-react";
import { useYouTube } from "../../hooks/useYouTube";

const TikTokIcon = ({ size = 22 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.91a8.16 8.16 0 0 0 4.77 1.52V7a4.85 4.85 0 0 1-1.84-.31z" />
  </svg>
);
const InstaIcon = ({ size = 22 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <rect x="3" y="3" width="18" height="18" rx="5" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" />
  </svg>
);
const FacebookIcon = ({ size = 22 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
    <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5 3.66 9.15 8.44 9.94v-7.03H7.9v-2.91h2.54V9.85c0-2.5 1.49-3.89 3.78-3.89 1.1 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56v1.88h2.78l-.45 2.91h-2.33V22c4.78-.79 8.43-4.94 8.43-9.94z"/>
  </svg>
);
const YouTubeIcon = ({ size = 22 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
    <path d="M23.5 6.5a3 3 0 0 0-2.1-2.1C19.5 4 12 4 12 4s-7.5 0-9.4.4A3 3 0 0 0 .5 6.5 31.4 31.4 0 0 0 0 12a31.4 31.4 0 0 0 .5 5.5 3 3 0 0 0 2.1 2.1C4.5 20 12 20 12 20s7.5 0 9.4-.4a3 3 0 0 0 2.1-2.1A31.4 31.4 0 0 0 24 12a31.4 31.4 0 0 0-.5-5.5zM9.6 15.5v-7l6.3 3.5z"/>
  </svg>
);

const buildStats = (ytSubs) => [
  { value: "258k+", label: "Abonnés TikTok", icon: <Music2 size={18} />, accent: "gold" },
  { value: "60k+", label: "Abonnés Facebook", icon: <Users size={18} />, accent: "electric" },
  { value: "16k+", label: "Abonnés Instagram", icon: <Heart size={18} />, accent: "gold" },
  { value: ytSubs, label: "Abonnés YouTube", icon: <YouTubeIcon size={18} />, accent: "red" },
  { value: "4M+", label: "Likes TikTok", icon: <Flame size={18} />, accent: "electric" },
];

const ACCENT = {
  gold: "bg-[#D4AF37]/10 text-[#D4AF37]",
  electric: "bg-[#2D6CFF]/10 text-[#4A86FF]",
  red: "bg-[#FF0000]/10 text-[#FF4D4D]",
};

const SOCIALS = [
  { name: "TikTok", handle: "@coulisse.dj", url: "https://www.tiktok.com/@coulisse.dj", icon: <TikTokIcon /> },
  { name: "Instagram", handle: "@dj_as_adriensebert", url: "https://www.instagram.com/dj_as_adriensebert/", icon: <InstaIcon /> },
  { name: "Facebook", handle: "Adrien Sebert (DJ AS)", url: "https://www.facebook.com/adriensebertdj", icon: <FacebookIcon /> },
  { name: "YouTube", handle: "DJ AS - Adrien Sebert", url: "https://www.youtube.com/channel/UCQ8QnDAse7pVx0htDp6vIAg", icon: <YouTubeIcon /> },
];

export const SocialProof = () => {
  const { subscribers } = useYouTube();
  const STATS = buildStats(subscribers);
  return (
    <section
      id="social"
      data-testid="social-proof-section"
      className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[var(--bg-surface)] border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <span className="eyebrow">Communauté</span>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              Une vraie audience, <span className="gold-text-gradient">de vrais fans</span>
            </h2>
            <p className="mt-4 text-zinc-400 max-w-2xl">
              Plus qu'un DJ : un créateur de contenu suivi par des centaines de milliers
              de passionnés autour des coulisses du métier.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="glass rounded-2xl p-6 md:p-8 hover:-translate-y-1 transition-transform duration-300 last:col-span-2 md:last:col-span-1"
            >
              <div
                className={`inline-flex items-center justify-center w-10 h-10 rounded-xl ${ACCENT[s.accent]}`}
              >
                {s.icon}
              </div>
              <div className="mt-5 text-3xl md:text-4xl font-bold text-white">
                {s.value}
              </div>
              <div className="mt-1 text-xs uppercase tracking-[0.22em] text-zinc-400">
                {s.label}
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
          {SOCIALS.map((s) => (
            <a
              key={s.name}
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`social-${s.name.toLowerCase()}`}
              className="group flex items-center gap-4 px-5 py-4 rounded-2xl border border-white/10 hover:border-[#D4AF37]/40 bg-white/[0.02] hover:bg-white/[0.04] transition"
            >
              <div className="text-white/80 group-hover:text-[#D4AF37] transition-colors">
                {s.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-white">{s.name}</div>
                <div className="text-xs text-zinc-400 truncate">{s.handle}</div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};
